import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Section component that hydrates itself with the merchant's recently-viewed
 * products. IDs live in localStorage; the actual cards are rendered server-side
 * by fetching this section against `/search?q=id:<id>` via the Section
 * Rendering API (same idiom the predictive search uses).
 *
 * @extends {Component<{}>}
 */
class RecentlyViewedProductsSection extends Component {
  #hydrated = false;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#hydrated) return;
    this.#hydrated = true;

    // In the theme editor we intentionally skip hydration so the merchant
    // always sees a consistent placeholder preview instead of cards pulled
    // from whatever products they happened to browse in the editor session.
    if (window.Shopify?.designMode) return;

    this.#hydrate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  async #hydrate() {
    const { sectionId, excludeProductId } = this.dataset;
    const maxProducts = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;

    if (!sectionId) return;

    const viewedIds = RecentlyViewed.getProducts().filter(
      /** @param {string} id */ (id) => id && id !== excludeProductId
    );

    if (viewedIds.length === 0) {
      this.#remove();
      return;
    }

    const ids = viewedIds.slice(0, maxProducts);
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      await sectionRenderer.renderSection(sectionId, { cache: false, url });

      const remainingCards = this.querySelectorAll('.resource-list__item').length;
      if (remainingCards === 0) {
        this.#remove();
        return;
      }

      const wrapper = this.closest('.shopify-section');
      if (wrapper instanceof HTMLElement) wrapper.hidden = false;
      this.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Recently viewed products error:', error);
      this.#remove();
    }
  }

  #remove() {
    const wrapper = this.closest('.shopify-section');
    if (wrapper instanceof HTMLElement) {
      wrapper.hidden = true;
      return;
    }
    this.hidden = true;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}
